import { PROVIDER_SEND_TURN_MAX_ATTACHMENTS, type ThreadId } from "@cmmd-forge/contracts";

import type {
  ComposerImageAttachment,
  ComposerFileAttachment,
  ComposerThreadDraftState,
  PersistedComposerImageAttachment,
  PersistedComposerFileAttachment,
} from "./composerDraftStore";
import type { TerminalContextDraft } from "./lib/terminalContext";
import {
  ensureInlineTerminalContextPlaceholders,
  normalizeTerminalContextText,
} from "./lib/terminalContext";

export interface ComposerStashContent {
  readonly prompt: string;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly files: ReadonlyArray<ComposerFileAttachment>;
  readonly attachments: ReadonlyArray<PersistedComposerImageAttachment>;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
}

export function prepareComposerStashRestore(
  drafts: Record<string, ComposerThreadDraftState>,
  threadKey: string,
  destinationThreadId: ThreadId,
  content: ComposerStashContent,
): {
  readonly previousDraft: ComposerThreadDraftState | undefined;
  readonly restoredDraft: ComposerThreadDraftState;
  readonly drafts: Record<string, ComposerThreadDraftState>;
} | null {
  const previousDraft = drafts[threadKey];
  if (previousDraft?.stagedSubmission) return null;
  const restoredDraft = mergeComposerStash(
    previousDraft ?? emptyComposerDraft(),
    content,
    destinationThreadId,
  );
  if (!restoredDraft) return null;
  return {
    previousDraft,
    restoredDraft,
    drafts: { ...drafts, [threadKey]: restoredDraft },
  };
}

function emptyComposerDraft(): ComposerThreadDraftState {
  return {
    prompt: "",
    images: [],
    files: [],
    nonPersistedImageIds: [],
    persistedAttachments: [],
    terminalContexts: [],
    modelSelectionByProvider: {},
    activeProvider: null,
    runtimeMode: null,
    interactionMode: null,
    stagedSubmission: null,
  };
}

export function composerImageDedupKey(image: ComposerImageAttachment): string {
  return `${image.mimeType}\u0000${image.sizeBytes}\u0000${image.name}`;
}

export function terminalContextDedupKey(context: TerminalContextDraft): string {
  return `${context.terminalId}\u0000${context.lineStart}\u0000${context.lineEnd}`;
}

export function normalizeTerminalContextForThread(
  threadId: ThreadId,
  context: TerminalContextDraft,
): TerminalContextDraft | null {
  const terminalId = context.terminalId.trim();
  const terminalLabel = context.terminalLabel.trim();
  const text = normalizeTerminalContextText(context.text);
  if (!terminalId || !terminalLabel) return null;
  const lineStart = Math.max(1, Math.floor(context.lineStart));
  return {
    ...context,
    threadId,
    terminalId,
    terminalLabel,
    lineStart,
    lineEnd: Math.max(lineStart, Math.floor(context.lineEnd)),
    text,
  };
}

export function normalizeTerminalContextsForThread(
  threadId: ThreadId,
  contexts: ReadonlyArray<TerminalContextDraft>,
): TerminalContextDraft[] {
  const ids = new Set<string>();
  const keys = new Set<string>();
  const normalized: TerminalContextDraft[] = [];
  for (const context of contexts) {
    const next = normalizeTerminalContextForThread(threadId, context);
    if (!next) continue;
    const key = terminalContextDedupKey(next);
    if (ids.has(next.id) || keys.has(key)) continue;
    normalized.push(next);
    ids.add(next.id);
    keys.add(key);
  }
  return normalized;
}

function mergeComposerStash(
  base: ComposerThreadDraftState,
  content: ComposerStashContent,
  destinationThreadId: ThreadId,
): ComposerThreadDraftState | null {
  const images = mergeStashedImages(base, content);
  if (!images) return null;
  const files = mergeStashedFiles(base.files ?? [], content.files);
  if (images.images.length + files.length > PROVIDER_SEND_TURN_MAX_ATTACHMENTS) return null;
  const terminalContexts = mergeStashedTerminalContexts(base, content, destinationThreadId);
  const prompt = [base.prompt, content.prompt].filter((value) => value.length > 0).join("\n");
  return {
    ...base,
    prompt: ensureInlineTerminalContextPlaceholders(prompt, terminalContexts.contexts.length),
    images: images.images,
    files,
    persistedAttachments: images.attachments,
    terminalContexts: terminalContexts.contexts,
    stashTerminalContexts: [
      ...(base.stashTerminalContexts ?? []),
      ...terminalContexts.restoredSnapshots,
    ],
  };
}

function mergeStashedFiles(
  base: ReadonlyArray<ComposerFileAttachment>,
  incoming: ReadonlyArray<ComposerFileAttachment>,
): ComposerFileAttachment[] {
  const ids = new Set(base.map((file) => file.id));
  return [...base, ...incoming.filter((file) => !ids.has(file.id))];
}

function mergeStashedImages(
  base: ComposerThreadDraftState,
  content: ComposerStashContent,
): {
  readonly images: ComposerImageAttachment[];
  readonly attachments: PersistedComposerImageAttachment[];
} | null {
  const incomingAttachmentsById = new Map(
    content.attachments.map((attachment) => [attachment.id, attachment]),
  );
  const images = [...base.images];
  const attachments = base.persistedAttachments.filter((attachment) =>
    base.images.some((image) => image.id === attachment.id),
  );
  const imageIds = new Set(images.map((image) => image.id));
  const imageKeys = new Set(images.map(composerImageDedupKey));

  for (const image of content.images) {
    const attachment = incomingAttachmentsById.get(image.id);
    if (!attachment) return null;
    const dedupKey = composerImageDedupKey(image);
    if (imageIds.has(image.id) || imageKeys.has(dedupKey)) continue;
    images.push(image);
    attachments.push(attachment);
    imageIds.add(image.id);
    imageKeys.add(dedupKey);
  }
  if (images.length > PROVIDER_SEND_TURN_MAX_ATTACHMENTS) return null;
  return { images, attachments };
}

function mergeStashedTerminalContexts(
  base: ComposerThreadDraftState,
  content: ComposerStashContent,
  destinationThreadId: ThreadId,
): {
  readonly contexts: TerminalContextDraft[];
  readonly restoredSnapshots: TerminalContextDraft[];
} {
  const contexts = [...base.terminalContexts];
  const terminalIds = new Set(contexts.map((context) => context.id));
  const terminalKeys = new Set(contexts.map(terminalContextDedupKey));
  const restoredSnapshots: TerminalContextDraft[] = [];
  for (const context of content.terminalContexts) {
    const normalized = normalizeTerminalContextForThread(destinationThreadId, context);
    if (
      !normalized ||
      terminalIds.has(normalized.id) ||
      terminalKeys.has(terminalContextDedupKey(normalized))
    ) {
      continue;
    }
    contexts.push(normalized);
    restoredSnapshots.push(normalized);
    terminalIds.add(normalized.id);
    terminalKeys.add(terminalContextDedupKey(normalized));
  }
  return { contexts, restoredSnapshots };
}

export function persistedStashMatchesDraft(
  persisted: {
    readonly prompt: string;
    readonly attachments: ReadonlyArray<PersistedComposerImageAttachment>;
    readonly files?: ReadonlyArray<PersistedComposerFileAttachment>;
    readonly terminalContexts?: ReadonlyArray<{ readonly id: string }>;
    readonly stashTerminalContexts?: ReadonlyArray<TerminalContextDraft>;
  },
  draft: ComposerThreadDraftState,
): boolean {
  return (
    persisted.prompt === draft.prompt &&
    JSON.stringify(persisted.attachments) === JSON.stringify(draft.persistedAttachments) &&
    JSON.stringify(persisted.files ?? []) ===
      JSON.stringify(
        (draft.files ?? []).map(({ file: _file, type: _type, ...attachment }) => attachment),
      ) &&
    JSON.stringify(persisted.terminalContexts ?? []) ===
      JSON.stringify(draft.terminalContexts.map(({ text: _text, ...context }) => context)) &&
    JSON.stringify(persisted.stashTerminalContexts ?? []) ===
      JSON.stringify(draft.stashTerminalContexts ?? [])
  );
}
